import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, XCircle, AlertTriangle, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

interface ImportResult {
  success: number;
  errors: Array<{ patient: string; error: string }>;
  warnings: string[];
}

interface ImportProgressProps {
  importing: boolean;
  progress: number;
  total: number;
  results: ImportResult;
  onBack: () => void;
}

export function ImportProgress({ 
  importing, 
  progress, 
  total, 
  results,
  onBack 
}: ImportProgressProps) {
  
  const progressPercentage = total > 0 ? (progress / total) * 100 : 0;
  const isComplete = progress === total && !importing;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">
            {importing ? "Importando Pacientes..." : "Importação Concluída"}
          </h2>
          <p className="text-muted-foreground">
            {importing 
              ? `Processando ${progress} de ${total} pacientes` 
              : "Confira o resultado da importação"
            }
          </p>
        </div>
        {isComplete && (
          <Button onClick={onBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Nova Importação
          </Button>
        )}
      </div>

      {/* Progress Card */}
      <Card>
        <CardHeader>
          <CardTitle>Progresso</CardTitle>
          <CardDescription>
            {progress} / {total} pacientes processados
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Progress 
            value={progressPercentage} 
            className={cn(
              "h-3 transition-all",
              importing && "animate-pulse"
            )} 
          />
          <div className="text-center text-2xl font-bold">
            {Math.round(progressPercentage)}%
          </div>
        </CardContent>
      </Card>

      {/* Results Summary */}
      {isComplete && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="border-green-500/50 bg-green-500/5">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <CardDescription>Sucesso</CardDescription>
              </div>
              <CardTitle className="text-3xl text-green-600 dark:text-green-400">
                {results.success}
              </CardTitle>
            </CardHeader>
          </Card>

          <Card className="border-yellow-500/50 bg-yellow-500/5">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                <CardDescription>Avisos</CardDescription>
              </div>
              <CardTitle className="text-3xl text-yellow-600 dark:text-yellow-400">
                {results.warnings.length}
              </CardTitle>
            </CardHeader>
          </Card>

          <Card className="border-red-500/50 bg-red-500/5">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-500" />
                <CardDescription>Erros</CardDescription>
              </div>
              <CardTitle className="text-3xl text-red-600 dark:text-red-400">
                {results.errors.length}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {/* Warnings */}
      {isComplete && results.warnings.length > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <div className="font-medium mb-2">Avisos durante importação:</div>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {results.warnings.map((warning, idx) => (
                <li key={idx}>{warning}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Errors */}
      {isComplete && results.errors.length > 0 && (
        <Card className="border-red-500/50">
          <CardHeader>
            <CardTitle className="text-red-600 dark:text-red-400 flex items-center gap-2">
              <XCircle className="h-5 w-5" />
              Erros Encontrados
            </CardTitle>
            <CardDescription>
              {results.errors.length} paciente{results.errors.length !== 1 ? 's' : ''} não foi importado
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[200px] overflow-auto">
              {results.errors.map((error, idx) => (
                <div key={idx} className="p-3 bg-muted rounded-lg">
                  <div className="font-medium text-sm">{error.patient}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {error.error}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Success Message */}
      {isComplete && results.success > 0 && (
        <Alert className="border-green-500/50 bg-green-500/5">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <AlertDescription className="text-green-700 dark:text-green-300">
            <div className="font-medium">
              {results.success} paciente{results.success !== 1 ? 's foram importados' : ' foi importado'} com sucesso!
            </div>
            <div className="text-sm mt-1">
              Você pode visualizar os pacientes na lista principal.
            </div>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
